/* eslint-disable @typescript-eslint/ban-ts-comment */
import { Box, Divider, Grid, Paper, Typography } from "@mui/material";
import { FC } from "react";

import __ from "web/Layout/Translations";

import isArrayHasItems from "web/utils/data/validator/array/isArrayHasItems";
import calculateSum from "web/utils/page/product/subscription/calculateSum";

import { pointsPerMonth, zlotyPerMonth } from "web/constants/benefits";

import { Classes } from "web/types/Common";
import {
  ISubscriptionCheckoutConfigurationStepTemplateProps,
  ISubscriptionTotals,
  ITotalsCalculatedItem,
  SubscriptionCheckoutSteps,
  SubscriptionCheckoutStepsTemplateProps,
  SubscriptionGroups,
} from "web/types/Subscription";

import { useAppContext } from "web/context/app";
import { useGetBanksQuery } from "web/features/pointsBank/pointsBankApiSlice";

import TotalRow from "./totalRow";
import preparePointsBanks from "./utils/preparePointsBanks";
import setAllPointsBank from "./utils/setAllPointsBanks";

interface ICheckoutSubscriptionDefaultAsideTotalsProps
  extends Pick<SubscriptionCheckoutStepsTemplateProps, "step" | "totals"> {
  classes?: Classes;
  group?: ISubscriptionCheckoutConfigurationStepTemplateProps["group"];
}

interface ICheckoutSubscriptionConfigurationStepAsideTotalsProps
  extends ICheckoutSubscriptionDefaultAsideTotalsProps {
  group?: ISubscriptionCheckoutConfigurationStepTemplateProps["group"];
}

type ICheckoutSubscriptionAsideTotalsProps =
  | ICheckoutSubscriptionDefaultAsideTotalsProps
  | ICheckoutSubscriptionConfigurationStepAsideTotalsProps;

const CheckoutSubscriptionAsideTotals: FC<
  ICheckoutSubscriptionAsideTotalsProps
> = ({ step, totals, group }) => {
  const { data: dataBanks } = useGetBanksQuery();
  const { items: availablePointsBanks } = dataBanks || {};
  const { isMobile } = useAppContext();
  const isConfigurationStep = step === SubscriptionCheckoutSteps.CONFIGURATION;
  const isDesktopSuccessPage =
    step === SubscriptionCheckoutSteps.SUCCESS && !isMobile;

  const {
    items,
    employeePriceSummary,
    employerPriceSummary,
    employeePayUPriceSummary,
    totalPriceSummary,
  } = totals;

  const [hasEmployeePrice, hasEmployerPrice, hasPayuPayment] = [
    employeePriceSummary,
    employerPriceSummary,
    employeePayUPriceSummary,
  ].map((price) => Boolean(price) && price !== "0.00");
  // financing sources should be hidden only if all of selected subs have hidden cost
  const hasHiddenCost = Number(totalPriceSummary) === 0;

  const pointsBanksArray =
    isConfigurationStep && group === SubscriptionGroups.UMS
      ? setAllPointsBank(items as ITotalsCalculatedItem[])
      : (totals as ISubscriptionTotals).pointsBanks || [];

  const pointsBanksResult = preparePointsBanks(
    pointsBanksArray,
    availablePointsBanks
  );

  const sourcesOfFinancing = [
    {
      isVisible: hasEmployerPrice,
      containerDataT1: "totals_aside_employer_price",
      titleDataT1: "employer_aside_text",
      titleValue: "Koszt pracodawcy",
      priceDataT1: "employer_price_summary",
      priceValue: employerPriceSummary,
      priceCurrency: zlotyPerMonth,
    },
    {
      isVisible: hasPayuPayment,
      containerDataT1: "totals_aside_payu_price",
      titleDataT1: "payu_aside_text",
      titleValue: "Płatne z PayU",
      priceDataT1: "payu_price_summary",
      priceValue: employeePayUPriceSummary,
      priceCurrency: zlotyPerMonth,
    },
    {
      isVisible: hasEmployeePrice,
      containerDataT1: "totals_aside_employee_price",
      titleDataT1: "employee_aside_text",
      titleValue: "Comiesięczne potrącenie z wynagrodzenia",
      priceDataT1: "employee_price_summary",
      priceValue: employeePriceSummary,
      priceCurrency: zlotyPerMonth,
    },
  ];

  return isArrayHasItems(items) ? (
    <Box>
      <Grid {...(isDesktopSuccessPage ? { container: true } : {})}>
        <Grid
          item
          {...(isDesktopSuccessPage
            ? { xs: 6, sx: { p: "0 40px 0 24px" } }
            : {})}
        >
          <Typography variant="subtitle1" sx={{ textTransform: "uppercase" }}>
            {__("Wybrane abonamenty")}
          </Typography>

          <Paper
            sx={{
              p: 0,
              boxShadow: "none",
            }}
          >
            <Grid container direction="column">
              {items?.map(
                ({
                  benefitId,
                  name,
                  employerPrice,
                  employeePrice,
                  employeePayUPrice,
                  pointsBankPrice,
                  points,
                }) => {
                  const summedPrice = calculateSum([
                    employerPrice,
                    employeePrice,
                    employeePayUPrice,
                    pointsBankPrice,
                    points,
                  ]);

                  return (
                    <TotalRow
                      containerKey={benefitId}
                      containerDataT1={name}
                      titleValue={name || ""}
                      priceValue={summedPrice}
                      priceCurrency="zł"
                    />
                  );
                }
              )}

              <TotalRow
                containerSx={{
                  backgroundColor: "#F5F5F5",
                  borderTop: "none",
                  p: "8px 16px",
                  borderRadius: "8px",
                }}
                titleValue="Wartość abonamentów"
                titleSx={{ alignItems: "center" }}
                titleVariant="subtitle2"
                priceValue={totalPriceSummary}
                priceCurrency="zł"
                priceSize="M"
              />
            </Grid>
          </Paper>
        </Grid>

        {!hasHiddenCost && (
          <Grid
            item
            {...(isDesktopSuccessPage
              ? { xs: 6, sx: { p: "0 24px 0 40px" } }
              : {})}
          >
            <Typography
              variant="subtitle1"
              sx={{ mt: "24px", textTransform: "uppercase" }}
              data-t1="totals_aside_text_title"
            >
              {__("Źródło finansowania")}
            </Typography>

            <Paper
              sx={{
                p: "0px",
                boxShadow: "none",
              }}
            >
              <Grid container direction="column">
                {sourcesOfFinancing[0].isVisible && (
                  <TotalRow {...sourcesOfFinancing[0]} />
                )}

                {isArrayHasItems(pointsBanksResult) &&
                  pointsBanksResult.map((bank) => (
                    <TotalRow
                      containerKey={bank?.pointsBankId}
                      containerDataT1="totals_aside_bank_price"
                      titleDataT1="totals_aside_bank_label"
                      titleValue={bank?.name}
                      titleVariant="body2"
                      priceDataT1="totals_aside_bank_price"
                      priceValue={bank?.pointsBankPrice}
                      priceCurrency={pointsPerMonth}
                    />
                  ))}

                {sourcesOfFinancing
                  .slice(1)
                  .map(
                    ({ isVisible, ...rest }) =>
                      isVisible && <TotalRow {...rest} />
                  )}
              </Grid>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  ) : (
    <Box>
      <Typography variant="subtitle1" sx={{ m: "16px 0" }}>
        {__("Nie wybrano jeszcze żadnego abonamentu")}
      </Typography>
      <Divider />
    </Box>
  );
};

export default CheckoutSubscriptionAsideTotals;
